/**
 * Chat Conversation Show Component
 * Displays chat conversation details with full message history
 */

import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  FunctionField,
  useRecordContext,
} from "react-admin";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Paper,
  Divider,
  useTheme,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { semantic, brand } from "@rekindle/shared/theme";

/**
 * Message bubble component
 */
function MessageBubble({
  message,
}: {
  message: { id: string; role: string; content: string; createdAt: string };
}) {
  const isUser = message.role === "user";
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Theme-aware colors for AI messages
  const aiBubbleBg = isDarkMode
    ? theme.palette.grey[800]
    : theme.palette.grey[100];
  const aiBubbleColor = isDarkMode
    ? theme.palette.grey[100]
    : theme.palette.text.primary;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isUser ? "row-reverse" : "row",
        gap: 1,
        mb: 2,
      }}
    >
      <Avatar
        sx={{
          bgcolor: isUser ? brand.primary : semantic.info,
          width: 32,
          height: 32,
        }}
      >
        {isUser ? (
          <PersonIcon fontSize="small" />
        ) : (
          <SmartToyIcon fontSize="small" />
        )}
      </Avatar>
      <Box sx={{ maxWidth: "70%" }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            bgcolor: isUser ? brand.primary : aiBubbleBg,
            color: isUser ? "white" : aiBubbleColor,
            borderRadius: 2,
            borderTopLeftRadius: isUser ? 16 : 4,
            borderTopRightRadius: isUser ? 4 : 16,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {message.content}
          </Typography>
        </Paper>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: "block",
            mt: 0.5,
            textAlign: isUser ? "right" : "left",
          }}
        >
          {new Date(message.createdAt).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Messages list component
 */
function MessagesList() {
  const record = useRecordContext();

  if (!record?.messages || record.messages.length === 0) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ textAlign: "center", py: 4 }}
      >
        No messages in this conversation
      </Typography>
    );
  }

  return (
    <Box sx={{ maxHeight: 600, overflowY: "auto", p: 2 }}>
      {record.messages.map((message: any) => (
        <MessageBubble key={message.id} message={message} />
      ))}
    </Box>
  );
}

/**
 * Title for the show page
 */
function ChatConversationTitle() {
  const record = useRecordContext();
  return (
    <span>
      Chat Conversation {record ? String(record.id).substring(0, 8) : ""}
    </span>
  );
}

export function ChatConversationShow() {
  return (
    <Show title={<ChatConversationTitle />}>
      <SimpleShowLayout>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Header */}
          <Typography variant="h5">Chat Conversation Details</Typography>

          {/* Conversation Info */}
          <Card variant="outlined">
            <CardContent>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                Conversation Information
              </Typography>
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Conversation ID
                  </Typography>
                  <TextField source="id" />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    User ID
                  </Typography>
                  <TextField source="userId" />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    User Email
                  </Typography>
                  <FunctionField
                    render={(record: any) => record.userEmail || "-"}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Message Count
                  </Typography>
                  <FunctionField
                    render={(record: any) => record.messageCount || 0}
                  />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Started
                  </Typography>
                  <DateField source="createdAt" showTime />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Last Activity
                  </Typography>
                  <FunctionField
                    render={(record: any) =>
                      record.lastMessageAt
                        ? new Date(record.lastMessageAt).toLocaleString()
                        : "-"
                    }
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Archived
                  </Typography>
                  <FunctionField
                    render={(record: any) =>
                      record.archivedAt
                        ? new Date(record.archivedAt).toLocaleString()
                        : "Active"
                    }
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card variant="outlined">
            <CardContent>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                gutterBottom
              >
                Conversation
              </Typography>
              <MessagesList />
            </CardContent>
          </Card>
        </Box>
      </SimpleShowLayout>
    </Show>
  );
}
